import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams, GridRowSelectionModel } from "@mui/x-data-grid";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useApiQuery } from "../../hooks/use-api-query";
import { useDebounce } from "../../hooks/use-debounce";
import {
  useAcceptSuggestedCheck,
  useBatchAcceptSuggestedChecks,
  useBatchDismissSuggestedChecks,
  useDismissSuggestedCheck,
  useSuggestedChecks,
} from "../../hooks/use-suggested-checks";
import type {
  AcceptSuggestedCheckRequest,
  CheckValidation,
  SuggestedCheck,
  Test,
} from "../../types";
import { DataGrid } from "../data-grid";
import { TestSelect } from "../test-select";

// Auth header patterns to identify auth headers in suggestions
const AUTH_HEADER_PATTERNS = [
  /^authorization$/i,
  /^x-api-key$/i,
  /^x-auth-/i,
  /^x-token/i,
  /^x-access-token$/i,
  /^api-key$/i,
  /^bearer$/i,
];

function isAuthHeader(name: string): boolean {
  return AUTH_HEADER_PATTERNS.some((pattern) => pattern.test(name));
}

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];

interface SuggestedChecksTabProps {
  appConfigId?: string;
}

interface AcceptDialogState {
  open: boolean;
  suggestion: SuggestedCheck | null;
}

interface BatchDialogState {
  open: boolean;
  mode: "accept" | "dismiss";
}

const ConfidenceBar: React.FC<{ score: number }> = ({ score }) => {
  const percentage = Math.round(score * 100);
  const color = score >= 0.8 ? "success" : score >= 0.5 ? "warning" : "error";

  return (
    <Tooltip title={`${percentage}% confidence`}>
      <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          color={color}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
        />
        <Typography variant="caption" sx={{ minWidth: 35 }}>
          {percentage}%
        </Typography>
      </Box>
    </Tooltip>
  );
};

const MethodChip: React.FC<{ method: string }> = ({ method }) => {
  const colorMap: Record<string, "success" | "primary" | "warning" | "error" | "default"> = {
    GET: "success",
    POST: "primary",
    PUT: "warning",
    PATCH: "warning",
    DELETE: "error",
  };

  return (
    <Chip
      label={method}
      size="small"
      color={colorMap[method] || "default"}
      sx={{ fontWeight: 600, minWidth: 60 }}
    />
  );
};

const ActionColumn: React.FC<{
  row: SuggestedCheck;
  onAccept: (suggestion: SuggestedCheck) => void;
  onDismiss: (suggestionId: string) => void;
  isDismissing: boolean;
}> = ({ row, onAccept, onDismiss, isDismissing }) => {
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Tooltip title="Accept and create check">
        <IconButton
          size="small"
          color="success"
          onClick={(e) => {
            e.stopPropagation();
            onAccept(row);
          }}
        >
          <CheckCircleIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Dismiss suggestion">
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(row.id);
          }}
          disabled={isDismissing}
        >
          <CancelIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Accept Dialog Component
const AcceptSuggestionDialog: React.FC<{
  open: boolean;
  suggestion: SuggestedCheck | null;
  onClose: () => void;
  onAccept: (suggestionId: string, data: AcceptSuggestedCheckRequest) => void;
  isAccepting: boolean;
}> = ({ open, suggestion, onClose, onAccept, isAccepting }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedHeaders, setSelectedHeaders] = useState<Set<string>>(new Set());
  const [testDependencyId, setTestDependencyId] = useState<string | undefined>();
  const [cacheTtlSeconds, setCacheTtlSeconds] = useState<number | undefined>();
  const [expectedStatusCode, setExpectedStatusCode] = useState<number>(200);

  // Reset form when suggestion changes
  React.useEffect(() => {
    if (suggestion) {
      // Generate a name from the URL
      try {
        const url = new URL(suggestion.url);
        const pathParts = url.pathname.split("/").filter(Boolean);
        const lastPart = pathParts[pathParts.length - 1] || "endpoint";
        setName(`${suggestion.method} ${lastPart}`);
      } catch {
        setName(`${suggestion.method} check`);
      }
      setDescription("");

      // Pre-select auth headers and suggested auth headers
      const authHeaders = new Set<string>();
      Object.keys(suggestion.headers).forEach((headerName) => {
        if (isAuthHeader(headerName) || suggestion.suggested_auth_headers.includes(headerName)) {
          authHeaders.add(headerName);
        }
      });
      // Also include content-type if present
      Object.keys(suggestion.headers).forEach((headerName) => {
        if (headerName.toLowerCase() === "content-type") {
          authHeaders.add(headerName);
        }
      });
      setSelectedHeaders(authHeaders);
      // Auto-set test dependency to the source test
      setTestDependencyId(suggestion.test_id);
      setCacheTtlSeconds(undefined);
      setExpectedStatusCode(200);
    }
  }, [suggestion]);

  if (!suggestion) return null;

  const headerNames = Object.keys(suggestion.headers);

  const handleHeaderToggle = (headerName: string) => {
    setSelectedHeaders((prev) => {
      const next = new Set(prev);
      if (next.has(headerName)) {
        next.delete(headerName);
      } else {
        next.add(headerName);
      }
      return next;
    });
  };

  const handleSelectAllHeaders = () => {
    setSelectedHeaders(new Set(headerNames));
  };

  const handleDeselectAllHeaders = () => {
    setSelectedHeaders(new Set());
  };

  const handleAccept = () => {
    const validation: CheckValidation = {
      expected_status_code: expectedStatusCode,
      ai_mode: "fast",
    };

    onAccept(suggestion.id, {
      name,
      description: description || undefined,
      selected_headers: Array.from(selectedHeaders),
      test_dependency_id: testDependencyId,
      dependency_cache_ttl_seconds: cacheTtlSeconds,
      validation,
    });
  };

  const isValid = name.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Accept Suggested Check</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Request Summary */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Creating check from:
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <MethodChip method={suggestion.method} />
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  wordBreak: "break-all",
                }}
              >
                {suggestion.url}
              </Typography>
            </Stack>
          </Box>

          <Divider />

          {/* Basic Info */}
          <TextField
            required
            label="Check Name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="A descriptive name for this check"
          />

          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText="Optional description of what this check monitors"
          />

          <Divider />

          {/* Header Selection */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">
                Request Headers ({selectedHeaders.size} selected)
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={handleSelectAllHeaders}>
                  Select All
                </Button>
                <Button size="small" onClick={handleDeselectAllHeaders}>
                  Clear
                </Button>
              </Stack>
            </Stack>

            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
              }}
            >
              {headerNames.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No headers to select
                </Typography>
              ) : (
                headerNames.map((headerName) => (
                  <FormControlLabel
                    key={headerName}
                    control={
                      <Checkbox
                        checked={selectedHeaders.has(headerName)}
                        onChange={() => handleHeaderToggle(headerName)}
                        size="small"
                      />
                    }
                    label={
                      <Stack direction="column" spacing={0}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          >
                            {headerName}
                          </Typography>
                          {(isAuthHeader(headerName) ||
                            suggestion.suggested_auth_headers.includes(headerName)) && (
                            <Chip
                              label="Auth"
                              size="small"
                              color="warning"
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                          )}
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.65rem",
                            color: "text.secondary",
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {suggestion.headers[headerName].length > 50
                            ? `${suggestion.headers[headerName].substring(0, 50)}...`
                            : suggestion.headers[headerName]}
                        </Typography>
                      </Stack>
                    }
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mb: 1,
                      width: "100%",
                    }}
                  />
                ))
              )}
            </Box>
            <FormHelperText>
              Auth headers are auto-selected. Headers will be included in the check request.
            </FormHelperText>
          </Box>

          <Divider />

          {/* Test Dependency */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Test Dependency (Optional)
            </Typography>
            <TestSelect
              label="Run test first for auth tokens"
              placeholder="Search for a test..."
              value={testDependencyId || null}
              onChange={(testId) => setTestDependencyId(testId || undefined)}
              appConfigId={suggestion.app_config_id}
              size="small"
              helperText="Select a test to run before this check to generate fresh auth tokens"
            />

            {testDependencyId && (
              <TextField
                label="Cache TTL (seconds)"
                size="small"
                fullWidth
                type="number"
                value={cacheTtlSeconds || ""}
                onChange={(e) =>
                  setCacheTtlSeconds(e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">sec</InputAdornment>,
                }}
                helperText="Leave empty to auto-detect from JWT expiration"
                sx={{ mt: 2 }}
              />
            )}
          </Box>

          <Divider />

          {/* Validation */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Validation
            </Typography>
            <TextField
              label="Expected Status Code"
              size="small"
              fullWidth
              type="number"
              value={expectedStatusCode}
              onChange={(e) => setExpectedStatusCode(parseInt(e.target.value, 10) || 200)}
              helperText="The HTTP status code expected from this endpoint"
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isAccepting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAccept} disabled={!isValid || isAccepting}>
          {isAccepting ? "Creating..." : "Create Check"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Batch Confirmation Dialog
const BatchSuggestedCheckDialog: React.FC<{
  open: boolean;
  mode: "accept" | "dismiss";
  suggestions: SuggestedCheck[];
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}> = ({ open, mode, suggestions, onClose, onConfirm, isLoading }) => {
  const isAccept = mode === "accept";
  const count = suggestions.length;
  const displaySuggestions = suggestions.slice(0, 5);
  const remaining = count - displaySuggestions.length;

  const getDisplayPath = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.pathname;
    } catch {
      return url;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {isAccept ? (
            <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
          ) : (
            <CancelIcon color="error" sx={{ fontSize: 20 }} />
          )}
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
            {isAccept
              ? `Accept ${count} Suggestion${count > 1 ? "s" : ""}`
              : `Dismiss ${count} Suggestion${count > 1 ? "s" : ""}`}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {isAccept
            ? `${count} check${count > 1 ? "s" : ""} will be created with default settings:`
            : `${count} suggestion${count > 1 ? "s" : ""} will be dismissed:`}
        </Typography>

        <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, mb: 2 }}>
          {displaySuggestions.map((s) => (
            <Stack key={s.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <MethodChip method={s.method} />
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getDisplayPath(s.url)}
              </Typography>
            </Stack>
          ))}
          {remaining > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              ...and {remaining} more
            </Typography>
          )}
        </Paper>

        {isAccept && (
          <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
            <strong>Default settings applied:</strong>
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 16 }}>
              <li>Name: Auto-generated from method + path</li>
              <li>Headers: All auth headers included</li>
              <li>Test dependency: Source test</li>
              <li>Expected status: 200</li>
            </ul>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isAccept ? "success" : "error"}
          onClick={onConfirm}
          disabled={isLoading}
          startIcon={isAccept ? <CheckCircleIcon /> : <CancelIcon />}
        >
          {isLoading
            ? isAccept
              ? "Creating..."
              : "Dismissing..."
            : isAccept
              ? "Accept All"
              : "Dismiss All"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Floating Action Bar
const FloatingActionBar: React.FC<{
  selectedCount: number;
  onAcceptAll: () => void;
  onDismissAll: () => void;
}> = ({ selectedCount, onAcceptAll, onDismissAll }) => {
  if (selectedCount === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          borderRadius: 2,
          backgroundColor: "text.primary",
          color: "white",
          px: 2,
          py: 1,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" sx={{ mr: 1, fontSize: 12 }}>
            {selectedCount} selected
          </Typography>

          <Tooltip title="Accept Selected">
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={onAcceptAll}
              startIcon={<CheckCircleIcon />}
              sx={{ fontSize: 12, py: 0.5 }}
            >
              Accept All
            </Button>
          </Tooltip>

          <Tooltip title="Dismiss Selected">
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={onDismissAll}
              startIcon={<CancelIcon />}
              sx={{
                fontSize: 12,
                py: 0.5,
                color: "white",
                borderColor: "rgba(255,255,255,0.3)",
                "&:hover": {
                  borderColor: "error.main",
                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                },
              }}
            >
              Dismiss
            </Button>
          </Tooltip>
        </Stack>
      </Paper>
    </Box>
  );
};

export const SuggestedChecksTab: React.FC<SuggestedChecksTabProps> = ({ appConfigId }) => {
  const nav = useNavigate();

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Search state
  const [methodFilter, setMethodFilter] = useState<string>("");
  const [pathFilter, setPathFilter] = useState<string>("");
  const debouncedPath = useDebounce(pathFilter, 300);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Dialog states
  const [acceptDialog, setAcceptDialog] = useState<AcceptDialogState>({
    open: false,
    suggestion: null,
  });
  const [batchDialog, setBatchDialog] = useState<BatchDialogState>({
    open: false,
    mode: "accept",
  });

  // Fetch suggestions with pagination and filters
  const { data, isLoading, refetch } = useSuggestedChecks({
    app_config_id: appConfigId,
    status: "pending",
    method: methodFilter || undefined,
    path: debouncedPath || undefined,
    limit: pageSize,
    offset: page * pageSize,
  });

  const suggestions = data?.suggestions ?? [];
  const total = data?.total ?? 0;

  // Get selected suggestions for batch operations
  const selectedSuggestions = useMemo(() => {
    return suggestions.filter((s) => selectedIds.includes(s.id));
  }, [suggestions, selectedIds]);

  // Fetch tests for source test name display
  const { data: tests } = useApiQuery<Test[]>("/tests");
  const testsMap = React.useMemo(() => {
    const map = new Map<string, Test>();
    tests?.forEach((test) => {
      map.set(test.id, test);
    });
    return map;
  }, [tests]);

  // Mutations
  const { mutate: acceptSuggestion, isPending: isAccepting } = useAcceptSuggestedCheck();
  const { mutate: dismissSuggestion, isPending: isDismissing } = useDismissSuggestedCheck();
  const { mutate: batchAccept, isPending: isBatchAccepting } = useBatchAcceptSuggestedChecks();
  const { mutate: batchDismiss, isPending: isBatchDismissing } = useBatchDismissSuggestedChecks();

  const handleAcceptClick = (suggestion: SuggestedCheck) => {
    setAcceptDialog({ open: true, suggestion });
  };

  const handleAcceptDialogClose = () => {
    setAcceptDialog({ open: false, suggestion: null });
  };

  const handleAccept = (suggestionId: string, requestData: AcceptSuggestedCheckRequest) => {
    acceptSuggestion(
      {
        ...requestData,
        _url: `/checks/suggested/${suggestionId}/accept`,
      },
      {
        onSuccess: (response) => {
          handleAcceptDialogClose();
          refetch();
          // API returns { check_id: string, status: string }
          const checkId = (response as unknown as { check_id: string }).check_id;
          nav(`/checks/${checkId}`);
        },
      },
    );
  };

  const handleDismiss = (suggestionId: string) => {
    dismissSuggestion(
      {
        _url: `/checks/suggested/${suggestionId}/dismiss`,
      },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
  };

  // Batch operations
  const handleBatchAcceptClick = () => {
    setBatchDialog({ open: true, mode: "accept" });
  };

  const handleBatchDismissClick = () => {
    setBatchDialog({ open: true, mode: "dismiss" });
  };

  const handleBatchDialogClose = () => {
    setBatchDialog({ open: false, mode: "accept" });
  };

  const handleBatchConfirm = () => {
    if (batchDialog.mode === "accept") {
      batchAccept(
        { suggestion_ids: selectedIds },
        {
          onSuccess: () => {
            handleBatchDialogClose();
            setSelectedIds([]);
            refetch();
          },
        },
      );
    } else {
      batchDismiss(
        { suggestion_ids: selectedIds },
        {
          onSuccess: () => {
            handleBatchDialogClose();
            setSelectedIds([]);
            refetch();
          },
        },
      );
    }
  };

  // Selection handler
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setSelectedIds(newSelection as string[]);
  };

  // Reset page when filters change
  React.useEffect(() => {
    setPage(0);
  }, []);

  const columns: GridColDef<SuggestedCheck>[] = [
    {
      field: "method",
      headerName: "Method",
      width: 90,
      renderCell: (params: GridRenderCellParams<SuggestedCheck>) => (
        <MethodChip method={params.value} />
      ),
    },
    {
      field: "url",
      headerName: "URL",
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams<SuggestedCheck>) => {
        let displayUrl = params.value;
        try {
          const url = new URL(params.value);
          displayUrl = url.pathname + (url.search || "");
        } catch {
          // Keep full URL if parsing fails
        }
        return (
          <Tooltip title={params.value}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayUrl}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "test_id",
      headerName: "Source Test",
      width: 180,
      renderCell: (params: GridRenderCellParams<SuggestedCheck>) => {
        const test = testsMap.get(params.value);
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="View test">
              <Link to={`/tests-main/tests/${params.value}`} onClick={(e) => e.stopPropagation()}>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {test?.name || "Unknown"}
                </Typography>
              </Link>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: "test_run_id",
      headerName: "Test Run",
      width: 100,
      renderCell: (params: GridRenderCellParams<SuggestedCheck>) => {
        return (
          <Tooltip title="View test run">
            <Link to={`/runs/${params.value}?tab=network`} onClick={(e) => e.stopPropagation()}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {params.value.slice(0, 8)}
              </Typography>
            </Link>
          </Tooltip>
        );
      },
    },
    {
      field: "suggested_auth_headers",
      headerName: "Auth Headers",
      width: 120,
      renderCell: (params: GridRenderCellParams<SuggestedCheck>) => {
        const count = params.value?.length || 0;
        if (count === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          );
        }
        return (
          <Tooltip title={params.value.join(", ")}>
            <Chip
              label={`${count} header${count > 1 ? "s" : ""}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Tooltip>
        );
      },
    },
    {
      field: "confidence_score",
      headerName: "Confidence",
      width: 140,
      renderCell: (params: GridRenderCellParams<SuggestedCheck>) => (
        <ConfidenceBar score={params.value} />
      ),
    },
    {
      field: "created_at",
      headerName: "Detected",
      type: "dateTime",
      width: 160,
      valueGetter: (value: string) => new Date(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 100,
      renderCell: (params: GridRenderCellParams<SuggestedCheck>) => (
        <ActionColumn
          row={params.row}
          onAccept={handleAcceptClick}
          onDismiss={handleDismiss}
          isDismissing={isDismissing}
        />
      ),
    },
  ];

  // Show empty state only when not loading and no results after filtering
  if (!isLoading && suggestions.length === 0 && !methodFilter && !debouncedPath) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No pending suggestions. Run a test with HAR capture enabled to generate API check
        suggestions automatically, or analyze an existing test run from the Network tab.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Search Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Method</InputLabel>
          <Select
            value={methodFilter}
            label="Method"
            onChange={(e) => setMethodFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {HTTP_METHODS.map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search by path..."
            value={pathFilter}
            onChange={(e) => setPathFilter(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {(methodFilter || pathFilter) && (
          <Button
            size="small"
            onClick={() => {
              setMethodFilter("");
              setPathFilter("");
            }}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      <DataGrid
        loading={isLoading}
        rows={suggestions}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={handleSelectionChange}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
          setPage(newPage);
          setPageSize(newPageSize);
        }}
        rowCount={total}
        pageSizeOptions={[10, 25, 50]}
        containerSx={{ height: "calc(100vh - 300px)" }}
        customSx={{
          "& .MuiDataGrid-checkboxInput": {
            width: "20px",
            height: "20px",
          },
          "& .MuiDataGrid-checkboxInput input": {
            width: "20px",
            height: "20px",
          },
          "& .MuiDataGrid-columnHeader:first-of-type": {
            width: "50px !important",
            minWidth: "50px !important",
            maxWidth: "50px !important",
            padding: "0px 8px",
          },
          "& .MuiDataGrid-cell:first-of-type": {
            width: "50px !important",
            minWidth: "50px !important",
            maxWidth: "50px !important",
            padding: "0px 8px",
            justifyContent: "center",
          },
        }}
      />

      {/* Individual Accept Dialog */}
      <AcceptSuggestionDialog
        open={acceptDialog.open}
        suggestion={acceptDialog.suggestion}
        onClose={handleAcceptDialogClose}
        onAccept={handleAccept}
        isAccepting={isAccepting}
      />

      {/* Batch Dialog */}
      <BatchSuggestedCheckDialog
        open={batchDialog.open}
        mode={batchDialog.mode}
        suggestions={selectedSuggestions}
        onClose={handleBatchDialogClose}
        onConfirm={handleBatchConfirm}
        isLoading={isBatchAccepting || isBatchDismissing}
      />

      {/* Floating Action Bar */}
      <FloatingActionBar
        selectedCount={selectedIds.length}
        onAcceptAll={handleBatchAcceptClick}
        onDismissAll={handleBatchDismissClick}
      />
    </Box>
  );
};

export default SuggestedChecksTab;
